import React, { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import ConfirmDialog from "@/components/ConfirmDialog";
import PageHeader, { EmptyState } from "@/components/PageHeader";

export default function Districts({ embedded = false }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", code: "" });
  const [del, setDel] = useState(null);

  const load = useCallback(async () => { const { data } = await api.get("/admin/districts"); setItems(data.items); }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ name: "", code: "" }); setOpen(true); };
  const openEdit = (d) => { setEditing(d); setForm({ name: d.name, code: d.code }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/admin/districts/${editing.id}`, form);
      else await api.post("/admin/districts", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async () => {
    try { await api.delete(`/admin/districts/${del.id}`); toast.success("Deleted"); setDel(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setDel(null); }
  };

  return (
    <div>
      {embedded ? (
        <div className="mb-4 flex justify-end"><Button onClick={openNew} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="new-district-btn"><Plus className="mr-2 h-4 w-4" /> Add district</Button></div>
      ) : (
        <PageHeader title="Districts" subtitle="Manage districts"
          actions={<Button onClick={openNew} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="new-district-btn"><Plus className="mr-2 h-4 w-4" /> Add district</Button>} />
      )}
      {items.length === 0 ? <EmptyState title="No districts" testId="districts-empty" /> : (
        <Card className="border-slate-200 shadow-sm overflow-hidden">
          <Table data-testid="districts-table">
            <TableHeader><TableRow className="bg-slate-50">{["Name", "Code", ""].map((h) => <TableHead key={h} className="text-xs font-bold uppercase tracking-wide text-slate-500">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {items.map((d) => (
                <TableRow key={d.id} data-testid={`district-row-${d.code}`}>
                  <TableCell className="font-semibold text-slate-800">{d.name}</TableCell>
                  <TableCell className="font-mono text-sm">{d.code}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(d)} data-testid={`edit-district-${d.code}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="text-red-500" onClick={() => setDel(d)} data-testid={`delete-district-${d.code}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="district-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit district" : "Add district"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="district-name-input" /></div>
            <div className="space-y-2"><Label>Code</Label><Input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} data-testid="district-code-input" /></div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="district-save-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Delete district?" description={del ? `${del.name} will be removed.` : ""} confirmText="Delete" destructive onConfirm={remove} />
    </div>
  );
}
